import { Check, X } from "lucide-react-native";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Modal,
  ScrollView,
  useWindowDimensions,
  View,
} from "react-native";

import {
  Button,
  ButtonText,
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
  HStack,
  Pressable,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import {
  Demographic,
  DemographicFilter,
  DemographicFiltersMap,
} from "@/types/resources";

interface DemographicFilterPanelProps {
  filters: DemographicFiltersMap;
  isOpen: boolean;
  onClose: () => void;
  onFiltersChange: (filters: DemographicFiltersMap) => void;
  resourceId: string;
  resourceType: "study" | "adhoc";
  runId: string;
}

const PANEL_WIDTH = 600;
const CHIP_KEYS: DemographicFilter[] = [
  "gender",
  "marital_status",
  "household_size",
  "income_range",
  "region_type",
];

const COUNTRY_REGION_VALUES: string[][] = [
  ["us", "ca", "mx"],
  ["br", "ar", "cl", "co", "pe", "uy", "py", "bo", "ec", "ve"],
  [
    "am",
    "at",
    "ba",
    "be",
    "bg",
    "ch",
    "cy",
    "cz",
    "de",
    "dk",
    "ee",
    "es",
    "fi",
    "fr",
    "gb",
    "ge",
    "gr",
    "hr",
    "hu",
    "ie",
    "is",
    "it",
    "li",
    "lt",
    "lu",
    "lv",
    "md",
    "me",
    "mt",
    "nl",
    "no",
    "pl",
    "pt",
    "ro",
    "rs",
    "ru",
    "se",
    "si",
    "sk",
    "tr",
    "ua",
  ],
  [
    "bd",
    "cn",
    "hk",
    "in",
    "id",
    "jp",
    "kg",
    "kr",
    "kz",
    "my",
    "pk",
    "sg",
    "vn",
  ],
  ["au", "nz"],
  ["ae", "eg", "ke", "ma", "ng", "sa"],
];

/* ── Sub-components ─────────────────────────────────────── */

const Chip: FC<{ isSelected: boolean; label: string; onPress: () => void }> = ({
  isSelected,
  label,
  onPress,
}) => (
  <Pressable onPress={onPress}>
    <View
      className={`rounded-full px-3 py-1.5 ${isSelected ? "bg-primary-500" : "bg-background-100"}`}
    >
      <Text
        className={`text-xs font-medium ${isSelected ? "text-white" : "text-typography-500"}`}
      >
        {label}
      </Text>
    </View>
  </Pressable>
);

const CheckboxRow: FC<{
  isSelected: boolean;
  label: string;
  onPress: () => void;
}> = ({ isSelected, label, onPress }) => (
  <Checkbox
    className="flex-row items-center gap-2 py-1"
    isChecked={isSelected}
    onChange={onPress}
    value={label}
  >
    <CheckboxIndicator className="data-[checked=true]:bg-primary-500 data-[checked=true]:border-primary-500">
      <CheckboxIcon as={Check} className="text-white" size="sm" />
    </CheckboxIndicator>
    <CheckboxLabel className="text-sm text-typography-700">
      {label}
    </CheckboxLabel>
  </Checkbox>
);

/* ── Section renderers ──────────────────────────────────── */

const sectionStyle = (isFirst: boolean) => ({
  borderTopColor: "rgb(var(--color-outline-200))",
  borderTopWidth: isFirst ? 0 : 1,
  gap: 12,
  paddingBottom: 20,
  paddingTop: isFirst ? 0 : 20,
});

const ChipSection: FC<{
  demographic: Demographic;
  isFirst: boolean;
  selected: string[];
  onToggle: (v: string) => void;
}> = ({ demographic, isFirst, selected, onToggle }) => (
  <VStack style={sectionStyle(isFirst)}>
    <Text className="text-sm font-medium text-typography-700">
      {demographic.label}
    </Text>
    <HStack className="flex-wrap gap-2">
      {demographic.options.map((option) => {
        const v = option.value as string;
        return (
          <Chip
            isSelected={selected.includes(v)}
            key={v}
            label={option.label}
            onPress={() => onToggle(v)}
          />
        );
      })}
    </HStack>
  </VStack>
);

const RegionGroupedSection: FC<{
  demographic: Demographic;
  isFirst: boolean;
  regions: { label: string; values: string[] }[];
  selected: string[];
  onToggle: (v: string) => void;
  onToggleRegion: (values: string[]) => void;
}> = ({
  demographic,
  isFirst,
  regions,
  selected,
  onToggle,
  onToggleRegion,
}) => {
  const optionLabelMap = useMemo(
    () => new Map(demographic.options.map((o) => [o.value as string, o.label])),
    [demographic.options]
  );
  const availableValues = useMemo(
    () => new Set(demographic.options.map((o) => o.value as string)),
    [demographic.options]
  );

  const visibleRegions = useMemo(
    () =>
      regions
        .map((r) => ({
          ...r,
          values: r.values.filter((v) => availableValues.has(v)),
        }))
        .filter((r) => r.values.length > 0),
    [regions, availableValues]
  );

  return (
    <VStack style={sectionStyle(isFirst)}>
      <Text className="text-sm font-medium text-typography-700">
        {demographic.label}
      </Text>
      <VStack className="gap-4">
        {visibleRegions.map((region) => {
          const allSelected = region.values.every((v) => selected.includes(v));
          return (
            <VStack key={region.label} className="gap-1">
              <Checkbox
                className="flex-row items-center gap-2 py-1"
                isChecked={allSelected}
                onChange={() => onToggleRegion(region.values)}
                value={region.label}
              >
                <CheckboxIndicator className="data-[checked=true]:bg-primary-500 data-[checked=true]:border-primary-500">
                  <CheckboxIcon as={Check} className="text-white" size="sm" />
                </CheckboxIndicator>
                <CheckboxLabel className="text-sm font-semibold text-typography-900">
                  {region.label}
                </CheckboxLabel>
              </Checkbox>
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  paddingLeft: 28,
                }}
              >
                {region.values.map((v) => (
                  <View key={v} style={{ width: "33.333%" }}>
                    <CheckboxRow
                      isSelected={selected.includes(v)}
                      label={optionLabelMap.get(v) ?? v}
                      onPress={() => onToggle(v)}
                    />
                  </View>
                ))}
              </View>
            </VStack>
          );
        })}
      </VStack>
    </VStack>
  );
};

/* ── Main component ─────────────────────────────────────── */

const DemographicFilterPanel: FC<DemographicFilterPanelProps> = ({
  filters,
  isOpen,
  onClose,
  onFiltersChange,
  resourceId,
  resourceType,
  runId,
}) => {
  const { i18n } = useLocalization();
  const { demographics, loading } = API.Metadata.useGetDemographics();

  const [draftFilters, setDraftFilters] =
    useState<DemographicFiltersMap>(filters);
  const [availableCountries, setAvailableCountries] = useState<Set<string>>(
    new Set()
  );
  const discoveredRef = useRef(false);

  const { width: windowWidth } = useWindowDimensions();
  const panelWidth = Math.min(PANEL_WIDTH, windowWidth);

  const [modalVisible, setModalVisible] = useState(false);
  const [slideAnim] = useState(() => new Animated.Value(PANEL_WIDTH));
  const [backdropAnim] = useState(() => new Animated.Value(0));

  // Mount the panel during render (not in an effect) as soon as it opens; the
  // animation effect below unmounts it once the close animation finishes.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) setModalVisible(true);
  }

  useEffect(() => {
    Animated.parallel([
      Animated.timing(slideAnim, {
        duration: isOpen ? 260 : 220,
        toValue: isOpen ? 0 : panelWidth,
        useNativeDriver: true,
      }),
      Animated.timing(backdropAnim, {
        duration: isOpen ? 260 : 220,
        toValue: isOpen ? 1 : 0,
        useNativeDriver: true,
      }),
    ]).start(() => {
      if (!isOpen) setModalVisible(false);
    });
  }, [backdropAnim, isOpen, panelWidth, slideAnim]);

  // Sync the draft filters from the prop during render (not in an effect) when
  // it changes; the initial value is seeded above.
  const [prevFilters, setPrevFilters] = useState(filters);
  if (prevFilters !== filters) {
    setPrevFilters(filters);
    setDraftFilters(filters);
  }

  const isAdhoc = resourceType === "adhoc";

  const {
    count: studyCount,
    loading: studyLoadingPersonas,
    personas: studyPersonas,
  } = API.Studies.useGetRunPersonas(
    isAdhoc ? "" : resourceId,
    runId,
    isAdhoc ? undefined : draftFilters
  );
  const {
    count: adhocCount,
    loading: adhocLoadingPersonas,
    personas: adhocPersonas,
  } = API.Adhoc.useGetRunPersonas(
    isAdhoc ? resourceId : "",
    runId,
    isAdhoc ? draftFilters : undefined
  );

  const personas = isAdhoc ? adhocPersonas : studyPersonas;

  useEffect(() => {
    if (discoveredRef.current || personas.length === 0) return;
    const countries = new Set(personas.map((p) => p.residency.country_code));
    setAvailableCountries(countries.size > 1 ? countries : new Set());
    discoveredRef.current = true;
  }, [personas]);

  const personaCount = isAdhoc ? adhocCount : studyCount;
  const loadingPersonas = isAdhoc ? adhocLoadingPersonas : studyLoadingPersonas;

  const toggleValue = useCallback((key: DemographicFilter, value: string) => {
    setDraftFilters((prev) => {
      const current = (prev[key] ?? []) as string[];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      const updated = { ...prev };
      if (next.length === 0) {
        delete updated[key];
      } else {
        (updated as Record<string, string[]>)[key] = next;
      }
      return updated;
    });
  }, []);

  const toggleRegionValues = useCallback(
    (key: DemographicFilter, values: string[]) => {
      setDraftFilters((prev) => {
        const current = (prev[key] ?? []) as string[];
        const allSelected = values.every((v) => current.includes(v));
        const next = allSelected
          ? current.filter((v) => !values.includes(v))
          : [...current, ...values.filter((v) => !current.includes(v))];
        const updated = { ...prev };
        if (next.length === 0) {
          delete updated[key];
        } else {
          (updated as Record<string, string[]>)[key] = next;
        }
        return updated;
      });
    },
    []
  );

  const filtersHaveChanged = useMemo(
    () => JSON.stringify(draftFilters) !== JSON.stringify(filters),
    [draftFilters, filters]
  );

  const demographicMap = useMemo(() => {
    const map = new Map<string, Demographic>();
    demographics.forEach((d) => map.set(d.value, d));
    return map;
  }, [demographics]);

  const countryDemographic = useMemo(() => {
    const d = demographicMap.get("country");
    if (!d || availableCountries.size === 0) return null;
    return {
      ...d,
      options: d.options.filter((o) =>
        availableCountries.has(o.value as string)
      ),
    };
  }, [demographicMap, availableCountries]);

  const countryRegions = useMemo(
    () => [
      {
        label: i18n.t("audienceBuilder.presetNorthAmerica"),
        values: COUNTRY_REGION_VALUES[0],
      },
      {
        label: i18n.t("audienceBuilder.presetSouthAmerica"),
        values: COUNTRY_REGION_VALUES[1],
      },
      {
        label: i18n.t("audienceBuilder.presetEurope"),
        values: COUNTRY_REGION_VALUES[2],
      },
      {
        label: i18n.t("audienceBuilder.presetAsia"),
        values: COUNTRY_REGION_VALUES[3],
      },
      {
        label: i18n.t("audienceBuilder.presetOceania"),
        values: COUNTRY_REGION_VALUES[4],
      },
      {
        label: i18n.t("audienceBuilder.presetAfrica"),
        values: COUNTRY_REGION_VALUES[5],
      },
    ],
    [i18n]
  );

  const chipDemographics = CHIP_KEYS.map((k) => demographicMap.get(k)).filter(
    Boolean
  ) as Demographic[];

  const draftFilterCount = Object.keys(draftFilters).length;

  return (
    <Modal
      animationType="none"
      onRequestClose={onClose}
      transparent
      visible={modalVisible}
    >
      <Animated.View
        style={{
          backgroundColor: "rgba(0,0,0,0.4)",
          bottom: 0,
          left: 0,
          opacity: backdropAnim,
          position: "absolute",
          right: 0,
          top: 0,
        }}
      >
        <Pressable onPress={onClose} style={{ flex: 1 }} />
      </Animated.View>

      <Animated.View
        style={{
          bottom: 0,
          position: "absolute",
          right: 0,
          top: 0,
          transform: [{ translateX: slideAnim }],
          width: panelWidth,
        }}
      >
        <VStack
          className="flex-1 bg-background-0 border-l border-outline-200"
          style={{
            shadowColor: "#000",
            shadowOffset: { height: 0, width: -4 },
            shadowOpacity: 0.08,
            shadowRadius: 12,
          }}
        >
          {/* Header */}
          <HStack className="items-center justify-between px-5 py-4 border-b border-outline-200">
            <Text className="text-base font-semibold text-typography-900">
              {i18n.t("runs.filters")}
            </Text>
            <HStack className="items-center gap-4">
              {draftFilterCount > 0 && (
                <Pressable onPress={() => setDraftFilters({})}>
                  <Text className="text-sm text-primary-500 font-medium">
                    {i18n.t("misc.reset")}
                  </Text>
                </Pressable>
              )}
              <Pressable onPress={onClose}>
                <X color="rgb(var(--color-typography-500))" size={18} />
              </Pressable>
            </HStack>
          </HStack>

          {/* Content */}
          {loading ? (
            <VStack className="flex-1 p-5 gap-5">
              {[1, 2, 3].map((i) => (
                <VStack key={i} className="gap-3">
                  <Skeleton className="h-4 w-32 rounded" />
                  <HStack className="gap-2 flex-wrap">
                    {[1, 2, 3, 4].map((j) => (
                      <Skeleton className="h-7 w-20 rounded-full" key={j} />
                    ))}
                  </HStack>
                </VStack>
              ))}
            </VStack>
          ) : (
            <ScrollView
              className="flex-1"
              contentContainerStyle={{ gap: 0, padding: 20 }}
              showsVerticalScrollIndicator={false}
            >
              {countryDemographic && (
                <RegionGroupedSection
                  demographic={countryDemographic}
                  isFirst
                  onToggle={(v) => toggleValue("country", v)}
                  onToggleRegion={(values) =>
                    toggleRegionValues("country", values)
                  }
                  regions={countryRegions}
                  selected={(draftFilters.country ?? []) as string[]}
                />
              )}

              {chipDemographics.map((demographic, idx) => (
                <ChipSection
                  demographic={demographic}
                  isFirst={!countryDemographic && idx === 0}
                  key={demographic.value}
                  onToggle={(v) =>
                    toggleValue(demographic.value as DemographicFilter, v)
                  }
                  selected={
                    (draftFilters[demographic.value as DemographicFilter] ??
                      []) as string[]
                  }
                />
              ))}
            </ScrollView>
          )}

          {/* Footer */}
          <VStack className="px-5 py-4 border-t border-outline-200">
            <Button
              className="bg-primary-500 rounded-xl"
              isDisabled={
                !filtersHaveChanged || loadingPersonas || personaCount === 0
              }
              onPress={() => {
                onFiltersChange(draftFilters);
                onClose();
              }}
            >
              <ButtonText className="text-white font-semibold">
                {loadingPersonas
                  ? i18n.t("runs.fetchingRespondents")
                  : i18n.t("runs.applyFilters", { count: personaCount })}
              </ButtonText>
            </Button>
          </VStack>
        </VStack>
      </Animated.View>
    </Modal>
  );
};

export default DemographicFilterPanel;
